/**
 * Vacantes Service
 * Servicio para gestionar las vacantes (Job Descriptions)
 */

import apiClient, { type ApiResponse } from './api.client';
import API_CONFIG from '@/config/api.config';
import type {
  VacanteResponse,
  VacanteDB,
  VacanteUpdate,
  VacantePublicView,
} from '@/types/backend.types';

class VacantesService {
  /**
   * Sube un archivo JD (PDF o DOCX) y lo procesa con IA
   * @param file - Archivo PDF o DOCX del Job Description
   * @returns Respuesta con los datos extraídos por la IA
   */
  async uploadJD(file: File): Promise<ApiResponse<VacanteResponse>> {
    // Validar tipo de archivo
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: 'Tipo de archivo no válido. Solo se permiten PDF o DOCX.',
      };
    }

    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'El archivo excede el tamaño máximo de 5MB.',
      };
    }

    return apiClient.uploadFile<VacanteResponse>(
      API_CONFIG.ENDPOINTS.VACANTES.UPLOAD_JD,
      file,
      undefined,
      { requiresAuth: true, timeout: API_CONFIG.UPLOAD_JD_TIMEOUT_MS }
    );
  }

  /**
   * Obtiene los detalles completos de una vacante
   * @param vacanteId - ID de la vacante
   */
  async getVacante(vacanteId: string): Promise<ApiResponse<VacanteDB>> {
    return apiClient.get<VacanteDB>(
      API_CONFIG.ENDPOINTS.VACANTES.GET_VACANTE(vacanteId)
    );
  }

  /**
   * Actualiza los datos de una vacante
   * @param vacanteId - ID de la vacante
   * @param data - Datos a actualizar
   */
  async updateVacante(
    vacante_id: string,
    data: VacanteUpdate
  ): Promise<ApiResponse<VacanteDB>> {
    return apiClient.put<VacanteDB>(
      API_CONFIG.ENDPOINTS.VACANTES.UPDATE_VACANTE(vacante_id),
      data
    );
  }

  /**
   * Actualiza únicamente el estado de una vacante
   * @param vacanteId - ID de la vacante
   * @param status - Nuevo estado
   */
  async updateStatus(
    vacanteId: string,
    status: string
  ): Promise<ApiResponse<any>> {
    return apiClient.patch<any>(
      API_CONFIG.ENDPOINTS.VACANTES.UPDATE_STATUS(vacanteId),
      { status }
    );
  }

  /**
   * Lista todas las vacantes del reclutador
   */
  async listVacantes(): Promise<ApiResponse<VacanteDB[]>> {
    return apiClient.get<VacanteDB[]>(API_CONFIG.ENDPOINTS.VACANTES.LIST_VACANTES);
  }

  /**
   * Publica una vacante (cambia estado a PUBLICADA)
   * @param vacanteId - ID de la vacante
   */
  async publishVacante(vacanteId: string): Promise<ApiResponse<VacanteDB>> {
    return apiClient.post<VacanteDB>(
      API_CONFIG.ENDPOINTS.VACANTES.PUBLISH_VACANTE(vacanteId)
    );
  }

  /**
   * Obtiene la vista pública de una vacante (para candidatos)
   * @param vacanteId - ID de la vacante
   */
  async getPublicVacante(vacanteId: string): Promise<ApiResponse<VacantePublicView>> {
    return apiClient.get<VacantePublicView>(
      API_CONFIG.ENDPOINTS.VACANTES.GET_PUBLIC_VACANTE(vacanteId),
      { requiresAuth: false }
    );
  }

  /**
   * Elimina una vacante
   * @param vacanteId - ID de la vacante a eliminar
   */
  async deleteVacante(vacanteId: string): Promise<ApiResponse<{ vacante_id: string; message: string }>> {
    return apiClient.delete<{ vacante_id: string; message: string }>(
      API_CONFIG.ENDPOINTS.VACANTES.DELETE_VACANTE(vacanteId)
    );
  }

  /**
   * [Reclutador] Mueve una candidatura de estado en el proceso.
   * Estados: postulado | en_revision | entrevistado | seleccionado | descartado
   */
  async updateEstadoCandidatura(
    vacanteId: string,
    candidateUid: string,
    estado: string
  ): Promise<ApiResponse<{ estado_proceso: string; estado_proceso_label: string }>> {
    return apiClient.patch<{ estado_proceso: string; estado_proceso_label: string }>(
      `/api/vacantes/${vacanteId}/candidatos/${candidateUid}/estado`,
      { estado_proceso: estado }
    );
  }

  /**
   * [Reclutador] Cierra la vacante contratando a alguien (o a nadie) y
   * descartando al resto. Cerrar y resolver van juntos a propósito: por separado
   * era como quedaban candidatos esperando indefinidamente.
   */
  async resolverVacante(
    vacanteId: string,
    seleccionadoUid: string | null
  ): Promise<ApiResponse<{ seleccionado_uid: string | null; descartados: number }>> {
    return apiClient.post<{ seleccionado_uid: string | null; descartados: number }>(
      `/api/vacantes/${vacanteId}/resolver`,
      { seleccionado_uid: seleccionadoUid }
    );
  }

  /**
   * [Reclutador] Obtiene los datos estructurados del CV de un candidato para una vacante.
   */
  async getCandidateCVData(
    vacanteId: string,
    candidateUid: string
  ): Promise<ApiResponse<{ extracted_data?: any }>> {
    return apiClient.get<{ extracted_data?: any }>(
      API_CONFIG.ENDPOINTS.VACANTES.GET_CANDIDATE_CV_DATA(vacanteId, candidateUid)
    );
  }

  /**
   * [Reclutador] Actualiza los datos estructurados del CV de un candidato.
   */
  async updateCandidateCVData(
    vacanteId: string,
    candidateUid: string,
    fullData: Record<string, unknown>
  ): Promise<ApiResponse<{ success?: boolean }>> {
    return apiClient.patch<{ success?: boolean }>(
      API_CONFIG.ENDPOINTS.VACANTES.UPDATE_CANDIDATE_CV_DATA(vacanteId, candidateUid),
      fullData
    );
  }
}

// Singleton instance
export const vacantesService = new VacantesService();
export default vacantesService;
